import { MapScoreResponse } from '../api/openapi'
import { EmployeeSort } from '../services/EmployeeService'
import { TeamService, TeamStatus } from '../services/TeamService'
import { FormCategory } from '../types/definitions/formCategory'
import { SortOrderEnum } from '../types/enums/sort-order.enum'
import { getValueFromLiteralPath } from './objectUtils'
import { simpleTextCompareFn } from './text'
import { cloneDeep } from './utils'

export type SortBy = { property: string; order: SortOrderEnum }

export type TeamSortType =
  | 'issues'
  | 'progress'
  | 'name'
  | 'leaders'
  | 'score'
  | 'default'
  | 'notStarted'
  | 'unfinished'
  | 'finished'
  | 'skipped'

// TODO: Same as SortBy remove one of them
export interface SortingType {
  property: TeamSortType
  order: SortOrderEnum
}

const asNumber = (value?: number): number => value ?? 0
const asProgress = (finished?: number, teamSize?: number): number => {
  const size = asNumber(teamSize)
  if (size <= 0) return 0
  return asNumber(finished) / size
}

export const initialSortingEmployees: EmployeeSort = {
  property: 'name',
  order: SortOrderEnum.Ascending,
}

export const sort = <T>(
  records: T[],
  sorting: SortBy[],
  customSortFunctions?: Partial<Record<string, (item1: T, item2: T) => number>>
): T[] => {
  const result = cloneDeep<T>(records)
  return result.sort((record1, record2) => {
    try {
      for (const sort of sorting) {
        const sortFn = customSortFunctions?.[sort.property]
        const sortResult = sortFn
          ? sortFn?.(record1, record2) || 0
          : simpleTextCompareFn(
              getValueFromLiteralPath<string>(record1, sort.property) || '',
              getValueFromLiteralPath<string>(record2, sort.property) || ''
            )
        if (sortResult !== 0) {
          return sort.order === 'asc' ? sortResult : sortResult * -1
        }
      }
      return 0
    } catch (e) {
      console.error('Sorting error', e)
      return 0
    }
  }) as T[]
}

export const sortTeamIssuesByIssues = (records: TeamStatus[], order: string) => {
  const results = cloneDeep(records)
  return results.sort((a: TeamStatus, b: TeamStatus) => {
    const finishedA = asNumber(a.finished)
    const finishedB = asNumber(b.finished)
    return order === 'desc' ? finishedA - finishedB : finishedB - finishedA
  })
}

export const sortTeamIssuesByProgress = (records: TeamStatus[], order: string) => {
  const results = cloneDeep(records)
  return results.sort((a: TeamStatus, b: TeamStatus) => {
    const progressA = asProgress(a.finished, a.teamSize)
    const progressB = asProgress(b.finished, b.teamSize)
    const finishedA = asNumber(a.finished)
    const finishedB = asNumber(b.finished)
    if (progressA === progressB) {
      return order === 'desc' ? finishedA - finishedB : finishedB - finishedA
    }
    if (order === 'desc') {
      return progressA > progressB ? 1 : -1
    }
    return progressA < progressB ? 1 : -1
  })
}

export const sortUsageReportByNumeric = (records: TeamStatus[], order: string, property: string) => {
  const results = cloneDeep(records)
  return results.sort((a: TeamStatus, b: TeamStatus) => {
    const progressA = asNumber((a as unknown as Record<string, number | undefined>)[property])
    const progressB = asNumber((b as unknown as Record<string, number | undefined>)[property])
    const finishedA = asNumber(a.finished)
    const finishedB = asNumber(b.finished)
    if (progressA === progressB) {
      return order === 'desc' ? finishedA - finishedB : finishedB - finishedA
    }
    if (order === 'desc') {
      return progressA > progressB ? 1 : -1
    }
    return progressA < progressB ? 1 : -1
  })
}

const sortByTypeTeamIssues = (type: TeamSortType) => (data: TeamStatus[], selectedSort: SortingType) => {
  switch (type) {
    case 'issues':
      return sortTeamIssuesByIssues(data, selectedSort.order)
    case 'progress':
      return sortTeamIssuesByProgress(data, selectedSort.order)
    default:
      return TeamService.sortTeamStatus(data, [selectedSort])
  }
}

export const sortData = (data: TeamStatus[], selectedSort: SortingType, initialSorting: SortingType) => {
  const typeToSort = selectedSort?.property as TeamSortType
  return sortByTypeTeamIssues(typeToSort)(data, selectedSort || initialSorting)
}

export const sortMapScores = (scores: MapScoreResponse[], sortBy: keyof MapScoreResponse): MapScoreResponse[] => {
  return scores.sort((a: MapScoreResponse, b: MapScoreResponse) => (b[sortBy] as number) - (a[sortBy] as number))
}

export const sortBySalaryGrade = (formCategories: FormCategory[]): FormCategory[] => {
  for (const category of formCategories) {
    for (const form of category.forms?.values() ?? []) {
      const match = form?.name?.match(/(?:Grade Level|GL)\s*(\d+)/i)
      form.salaryGrade = match ? parseInt(match[1], 10) : -1
    }
    category.forms?.sort((a, b) => (a.salaryGrade ?? -1) - (b.salaryGrade ?? -1))
  }
  return formCategories
}
